import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { clearStoredUsername, getStoredUsername, saveStoredUsername } from '../../utils/usernameStorage';

const DailyQuiz = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [totalTime, setTotalTime] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [noQuizToday, setNoQuizToday] = useState(false);
  const [questionDetails, setQuestionDetails] = useState([]);
  const [showAnswerReview, setShowAnswerReview] = useState(false);
  const [hasTimeLimit, setHasTimeLimit] = useState(true);
  const [quizMode, setQuizMode] = useState('date');
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  
  // Category selection states
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCategorySelection, setShowCategorySelection] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  // Only check username on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    checkAndLoadUsername();
  }, []);

  // Only fetch quiz when category is selected and confirmed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (userName && !showNameInput && categoriesLoaded && selectedCategory && !showCategorySelection) {
      fetchTodaysQuiz();
    }
  }, [userName, showNameInput, categoriesLoaded, selectedCategory, showCategorySelection]);

  // Timer effect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      hasTimeLimit &&
      timeLeft > 0 &&
      !quizCompleted &&
      !showNameInput &&
      !alreadyCompleted &&
      !showCategorySelection
    ) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (hasTimeLimit && timeLeft === 0 && !quizCompleted) {
      submitQuiz();
    }
  }, [timeLeft, quizCompleted, showNameInput, alreadyCompleted, hasTimeLimit, showCategorySelection]);

  const checkAndLoadUsername = () => {
    const savedUsername = getStoredUsername();
    if (savedUsername) {
      setUserName(savedUsername);
      setShowNameInput(false);
      fetchAvailableCategories();
    } else {
      setShowNameInput(true);
      setLoading(false);
    }
  };

  const fetchAvailableCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/quiz/available-categories`);
      
      if (response.data.categories && response.data.categories.length > 0) {
        setAvailableCategories(response.data.categories);
        setQuizMode(response.data.mode);
        setCategoriesLoaded(true);
        setShowCategorySelection(true);
        setLoading(false);
      } else {
        setNoQuizToday(true);
        setCategoryError('No quiz questions available today. Please check back later!');
        setLoading(false);
        setCategoriesLoaded(false);
      }
    } catch (error) {
      console.error('Error fetching available categories:', error);
      setNoQuizToday(true);
      setCategoryError('Failed to load quiz categories. Please try again later.');
      setLoading(false);
      setCategoriesLoaded(false);
    }
  };

  const handleCategorySelect = (categoryName) => {
    setSelectedCategory(categoryName);
    setCategoryError('');
  };

  const handleCategoryContinue = () => {
    if (!selectedCategory) {
      setCategoryError('Please select a category to continue');
      return;
    }
    
    // Hide category selection and trigger quiz fetch
    setShowCategorySelection(false);
    setLoading(true);
  };

  const fetchTodaysQuiz = async () => {
    try {
      const url = selectedCategory
        ? `${API_BASE_URL}/api/quiz/daily?category=${selectedCategory}`
        : `${API_BASE_URL}/api/quiz/daily`;

      const response = await axios.get(url, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.questions) {
        setQuestions(response.data.questions);
        setQuizMode(response.data.mode || 'date');
        setAttemptsRemaining(response.data.attemptsRemaining || null);

        const timeLimit = response.data.timeLimit;
        const hasLimit = response.data.hasTimeLimit !== false && timeLimit !== null;

        setHasTimeLimit(hasLimit);
        if (hasLimit) {
          setTimeLeft(timeLimit);
          setTotalTime(timeLimit);
        }
      }
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 400) {
        if (error.response.data.noQuiz) {
          setNoQuizToday(true);
          setCategoryError(error.response.data.message);
        } else {
          setAlreadyCompleted(true);
        }
        setLoading(false);
      } else {
        console.error('Error fetching quiz:', error);
        alert(`Failed to load quiz: ${error.response?.data?.message || error.message}`);
        setLoading(false);
      }
    }
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    const username = saveStoredUsername(userName);
    if (username) {
      setUserName(username);
      setShowNameInput(false);
      fetchAvailableCategories();
    }
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: answerIndex,
    });
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      submitQuiz();
    }
  };

  const submitQuiz = async () => {
    setQuizCompleted(true);

    try {
      const timeSpent = hasTimeLimit ? totalTime - timeLeft : 0;
      const response = await axios.post(
        `${API_BASE_URL}/api/quiz/submit`,
        {
          answers: selectedAnswers,
          timeSpent: timeSpent,
          userName: userName.trim(),
          mode: quizMode,
          category: selectedCategory && selectedCategory !== 'all' ? selectedCategory : 'mix',
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setScore(response.data.result.score);
      setQuestionDetails(response.data.result.questionDetails || []);
      setAttemptsRemaining(response.data.attemptsRemaining || null);
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert(`Failed to submit quiz: ${error.response?.data?.message || error.message}`);
    }
  };

  const formatTime = (seconds) => {
    if (!hasTimeLimit || seconds === null) return 'No limit';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const shareScore = (platform) => {
    const totalMarks = questions.length * 10;
    const quizType = quizMode === 'pool' ? 'Daily Quiz' : "Today's Quiz";
    const categoryText =
      selectedCategory && selectedCategory !== 'mix'
        ? ` (${availableCategories.find((c) => c.name === selectedCategory)?.displayName || selectedCategory})`
        : selectedCategory === 'mix'
        ? ' (Mixed Categories)'
        : '';
    const text = `I scored ${score}/${totalMarks} marks in ${quizType}${categoryText}! Can you beat my score?`;
    const url = window.location.href;

    if (platform === 'facebook') {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
        '_blank'
      );
    } else if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
    }
  };

  // Name Input Screen
  if (showNameInput) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-2 sm:p-4 relative">
        <Link 
          to="/" 
          className="fixed top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all bg-white shadow-lg z-50"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Back to Home</span>
          <span className="sm:hidden">Back</span>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 w-full max-w-xs sm:max-w-md">
          <div className="text-center mb-4 sm:mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">Daily Sports Quiz</h1>
            <p className="text-sm sm:text-base text-gray-600">Test your sports knowledge!</p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2">Your name will be saved for future use</p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4 sm:p-5 mb-4 sm:mb-6">
            <div className="grid grid-cols-2 gap-4 sm:gap-6 text-xs sm:text-sm">
              <div className="flex items-center gap-2 sm:gap-3 p-2 bg-white rounded-md shadow-sm">
                <span className="text-blue-600 text-base sm:text-lg">⏱️</span>
                <span className="font-medium text-gray-700">Timer varies</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 p-2 bg-white rounded-md shadow-sm">
                <span className="text-blue-600 text-base sm:text-lg">📝</span>
                <span className="font-medium text-gray-700">Multiple questions</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 p-2 bg-white rounded-md shadow-sm">
                <span className="text-blue-600 text-base sm:text-lg">🎯</span>
                <span className="font-medium text-gray-700">10 marks each</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 p-2 bg-white rounded-md shadow-sm">
                <span className="text-blue-600 text-base sm:text-lg">🔄</span>
                <span className="font-medium text-gray-700">Daily quiz</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleNameSubmit}>
            <div className="mb-4 sm:mb-6">
              <label htmlFor="userName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Enter your name to start
              </label>
              <input
                id="userName"
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                placeholder="Your name"
                required
                maxLength="50"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base"
            >
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Category Selection Screen
  if (showCategorySelection && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-2 sm:p-4 relative">
        <Link 
          to="/" 
          className="fixed top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all bg-white shadow-lg z-50"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Back to Home</span>
          <span className="sm:hidden">Back</span>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 w-full max-w-xs sm:max-w-2xl">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2">Choose Your Category</h2>
            <p className="text-sm sm:text-base text-gray-600">Select a category to start the quiz</p>
            <p className="text-xs sm:text-sm text-blue-600 mt-2">Welcome back, {userName}! 👋</p>
            <div className="mt-3 inline-flex items-center bg-blue-50 px-3 py-1 rounded-full">
              {/* <span className="text-xs sm:text-sm text-blue-700">
                🔄 {quizMode === 'pool' ? 'Random Question Pool' : 'Daily Quiz'}
              </span> */}
            </div>
            {/* {maxAttempts !== null && (
              <div className="mt-2 inline-flex items-center bg-orange-50 px-3 py-1 rounded-full">
                <span className="text-xs sm:text-sm text-orange-700">
                  🎯 Max {maxAttempts} attempt{maxAttempts !== 1 ? 's' : ''} per day
                </span>
              </div>
            )} */}
          </div>

          {categoryError && (
            <div className="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-400 p-3 sm:p-4 rounded">
              <p className="text-xs sm:text-sm text-red-700">{categoryError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {availableCategories.map((category) => (
              <button
                key={category.name}
                onClick={() => handleCategorySelect(category.name)}
                className={`p-4 sm:p-6 rounded-xl border-2 transition-all transform hover:scale-105 ${
                  selectedCategory === category.name
                    ? 'border-blue-600 bg-blue-50 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xl sm:text-2xl">
                    {category.name === 'mix' ? '🎯' :
                     category.name === 'football' ? '⚽' :
                     category.name === 'cricket' ? '🏏' :
                     category.name === 'basketball' ? '🏀' :
                     category.name === 'tennis' ? '🎾' :
                     category.name === 'hockey' ? '🏑' :
                     category.name === 'rugby' ? '🏉' :
                     category.name === 'golf' ? '⛳' :
                     category.name === 'boxing' ? '🥊' : 
                     category.name === 'general' ? '📚' : '🏆'}
                  </span>
                  {selectedCategory === category.name && (
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <h3 className="font-bold text-base sm:text-lg text-gray-800">{category.displayName}</h3>
                <div className="mt-2 space-y-1">
                  <p className="text-xs sm:text-sm text-blue-600 font-semibold">
                    {category.questionCount} question{category.questionCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {quizMode === 'pool' && selectedCategory === 'mix' && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <p className="text-sm text-blue-800">
                📊 Total questions in this quiz: <strong>{availableCategories.find(c => c.name === 'mix')?.questionCount || 0}</strong>
              </p>
            </div>
          )}

          <button
            onClick={handleCategoryContinue}
            disabled={!selectedCategory}
            className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-semibold text-white transition-all shadow-lg text-sm sm:text-base ${
              selectedCategory
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {selectedCategory ? 'Start Quiz →' : 'Select a Category'}
          </button>

          <button
            onClick={() => {
              setShowCategorySelection(false);
              setShowNameInput(true);
              setAvailableCategories([]);
              setSelectedCategory('');
              setCategoriesLoaded(false);
              clearStoredUsername();
            }}
            className="w-full mt-3 sm:mt-4 text-gray-600 hover:text-gray-800 font-medium text-sm sm:text-base"
          >
            ← Change Name
          </button>
        </div>
      </div>
    );
  }

  // Loading Screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-2 sm:p-4 relative">
        <Link 
          to="/" 
          className="fixed top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all bg-white shadow-lg z-50"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Back to Home</span>
          <span className="sm:hidden">Back</span>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-3 sm:mb-4"></div>
          <p className="text-sm sm:text-base text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  // No Quiz Available Screen
  if (noQuizToday) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-2 sm:p-4 relative">
        <Link 
          to="/" 
          className="fixed top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all bg-white shadow-lg z-50"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Back to Home</span>
          <span className="sm:hidden">Back</span>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center max-w-xs sm:max-w-md w-full">
          <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">📅</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">No Quiz Available</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            {categoryError || "There's no quiz available. Please try again later!"}
          </p>
          {availableCategories.length > 0 && (
            <button
              onClick={() => {
                setNoQuizToday(false);
                setShowCategorySelection(true);
                setCategoryError('');
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 sm:py-2 sm:px-6 rounded-lg transition-colors text-sm sm:text-base mb-3"
            >
              Choose Different Category
            </button>
          )}
        </div>
      </div>
    );
  }

  // Already Completed Screen
  if (alreadyCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-2 sm:p-4 relative">
        <Link 
          to="/" 
          className="fixed top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all bg-white shadow-lg z-50"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Back to Home</span>
          <span className="sm:hidden">Back</span>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center max-w-xs sm:max-w-md w-full">
          <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">✅</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">Quiz Limit Reached</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            {quizMode === 'pool' 
              ? "You've completed today's quiz. Come back tomorrow for new questions!"
              : "You've reached the maximum attempts for today's quiz. Try again tomorrow!"
            }
          </p>
          {attemptsRemaining !== null && attemptsRemaining > 0 && (
            <p className="text-xs sm:text-sm text-orange-600 font-medium mb-3">
              Attempts remaining: {attemptsRemaining}
            </p>
          )}
          {score > 0 && (
            <p className="text-xs sm:text-sm text-blue-600 font-medium">
              Your score: {score}/100 marks
            </p>
          )}
        </div>
      </div>
    );
  }

  // Results Screen
  if (showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4 relative">
        <Link 
          to="/" 
          className="fixed top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all bg-white shadow-lg z-50"
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Back to Home</span>
          <span className="sm:hidden">Back</span>
        </Link>

        <div className="max-w-xs sm:max-w-2xl mx-auto pt-12 sm:pt-16">
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
            <div className="text-center mb-6 sm:mb-8">
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">
                {score >= 70 ? '🏆' : score >= 50 ? '🎉' : '📚'}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">Quiz Completed!</h2>
              {selectedCategory && (
                <p className="text-xs sm:text-sm text-blue-600 mb-2">
                  Category: {selectedCategory === 'mix' ? 'Mixed Categories' : 
                    availableCategories.find(c => c.name === selectedCategory)?.displayName || selectedCategory}
                </p>
              )}
              <div className="text-xl sm:text-2xl font-bold text-blue-600 mb-2 sm:mb-4">
                Your Score: {score}/{questions.length * 10}
              </div>
              <div className="text-base sm:text-lg text-gray-600">
                {score >= 80 ? 'Excellent! 🌟' : 
                 score >= 60 ? 'Good job! 👍' : 
                 score >= 40 ? 'Not bad! 🙂' : 'Keep practicing! 💪'}
              </div>
              {attemptsRemaining !== null && attemptsRemaining > 0 && (
                <p className="text-xs sm:text-sm text-orange-600 font-medium mt-2">
                  You have {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining today
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <button
                onClick={() => shareScore('facebook')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                Share on Facebook
              </button>
              <button
                onClick={() => shareScore('whatsapp')}
                className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                Share on WhatsApp
              </button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-0">Review Your Answers</h3>
                <button
                  onClick={() => setShowAnswerReview(!showAnswerReview)}
                  className="text-blue-600 hover:text-blue-800 text-sm sm:text-base"
                >
                  {showAnswerReview ? 'Hide' : 'Show'} Details
                </button>
              </div>

              {showAnswerReview && (
                <div className="space-y-3 sm:space-y-4">
                  {questionDetails.map((detail, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-3 sm:pl-4 py-2">
                      <p className="font-medium mb-2 text-sm sm:text-base">{index + 1}. {detail.question}</p>
                      <div className="space-y-1 text-xs sm:text-sm">
                        {detail.options.map((option, optIndex) => (
                          <div key={optIndex} className={`flex items-center gap-2 p-2 rounded ${
                            optIndex === detail.correctAnswer ? 'bg-green-100 text-green-800' :
                            optIndex === detail.userAnswer ? 'bg-red-100 text-red-800' : 'bg-gray-50'
                          }`}>
                            <span className="font-medium">{String.fromCharCode(65 + optIndex)}.</span>
                            <span className="flex-1">{option}</span>
                            {optIndex === detail.correctAnswer && <span className="ml-auto">✓ Correct</span>}
                            {optIndex === detail.userAnswer && detail.userAnswer !== detail.correctAnswer && (
                              <span className="ml-auto">❌ Your answer</span>
                            )}
                            {detail.userAnswer === -1 && optIndex === detail.correctAnswer && (
                              <span className="ml-auto text-orange-600">⚠️ No answer</span>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-gray-600">
                        You scored: <strong>{detail.marks}/10</strong>
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="text-center mt-6 sm:mt-8">
              <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
                {quizMode === 'pool' 
                  ? 'Come back tomorrow for new random questions!'
                  : 'Come back tomorrow for a new quiz!'}
              </p>
              <div className="space-x-4">
                <Link
                  to="/leaderboard"
                  className="inline-block bg-purple-600 hover:bg-purple-700 text-white px-4 sm:px-6 py-2 rounded-lg transition-colors text-sm sm:text-base"
                >
                  View Leaderboard
                </Link>
                <button
                  onClick={() => window.location.href = '/'}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 sm:px-6 sm:py-2 rounded-lg transition-colors text-xs sm:text-base"
                >
                  <span className="sm:hidden">Back</span>
                  <span className="hidden sm:inline">Back to Home</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Questions Screen
  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4 relative">
      <Link 
        to="/" 
        className="fixed top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all bg-white shadow-lg z-50"
      >
        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="hidden sm:inline">Back to Home</span>
        <span className="sm:hidden">Back</span>
      </Link>

      <div className="max-w-xs sm:max-w-2xl mx-auto pt-12 sm:pt-16">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-blue-600 text-white p-4 sm:p-6">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Daily Sports Quiz</h1>
                {selectedCategory && (
                  <p className="text-xs opacity-90 mt-1">
                    📁 {selectedCategory === 'mix' ? 'Mixed Categories' : 
                      availableCategories.find(c => c.name === selectedCategory)?.displayName || selectedCategory}
                  </p>
                )}
                {/* {quizMode === 'pool' && (
                  <p className="text-xs opacity-90 mt-1">🔄 Random Question Pool</p>
                )} */}
              </div>
              <div className="text-right">
                {hasTimeLimit ? (
                  <>
                    <div className="text-xs sm:text-sm opacity-90">Time Left</div>
                    <div className={`text-lg sm:text-xl font-bold ${timeLeft < 60 ? 'text-red-300' : ''}`}>
                      {formatTime(timeLeft)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs sm:text-sm opacity-90">No Time Limit</div>
                    <div className="text-lg sm:text-xl font-bold">∞</div>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex justify-between text-xs sm:text-sm opacity-90 mb-2">
              <span>Question {currentQuestion + 1} of {questions.length}</span>
              <span className="truncate ml-2 max-w-24 sm:max-w-none">{userName}</span>
            </div>
            
            <div className="w-full bg-blue-500 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex-1">
                  {currentQ?.question}
                </h2>
                {currentQ?.category && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full ml-2 flex-shrink-0">
                    {currentQ.category}
                  </span>
                )}
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                {currentQ?.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(currentQuestion, index)}
                    className={`w-full text-left p-3 sm:p-4 rounded-lg border-2 transition-colors ${
                      selectedAnswers[currentQuestion] === index
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <span className="font-medium mr-2 sm:mr-3 text-sm sm:text-base">
                        {String.fromCharCode(65 + index)}.
                      </span>
                      <span className="text-sm sm:text-base flex-1">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between sm:items-center space-y-3 sm:space-y-0">
              <div className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
                {selectedAnswers[currentQuestion] !== undefined ? (
                  <span className="text-green-600">✓ Answer selected</span>
                ) : (
                  <span className="text-orange-600">⚠️ No answer selected</span>
                )}
              </div>
              
              <button
                onClick={nextQuestion}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base order-1 sm:order-2"
              >
                {currentQuestion === questions.length - 1 ? 'Submit Quiz' : 'Next Question'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyQuiz;
